/**
 * A dataset file → eval case drafts.
 *
 * Writing cases one form at a time does not scale past a handful; a real eval
 * set is a spreadsheet someone already has. This reads CSV, JSON (an array of
 * objects) and JSONL, finds the columns by the names people actually give them,
 * and returns drafts the import dialog can preview before anything is created.
 *
 * Nothing here talks to the API — parsing is pure, so a bad file is reported in
 * the preview rather than half-imported.
 */

export type Row = Record<string, unknown>;

export interface CaseDraft {
  row: number;                    // 1-based line in the file, for error messages
  caseCode: string;
  name: string;
  input: string;
  expectedResult: string | null;
  expectedSources: string[];
  context: string[];
  requiredFacts: string[];
  expectedToolNames: string[];
  tags: string[];
}

export interface ParsedDataset {
  drafts: CaseDraft[];
  /** Rows that cannot become a case, with the reason. */
  skipped: { row: number; reason: string }[];
  /** Which file column fed which case field — shown so a wrong guess is visible. */
  mapping: Partial<Record<keyof CaseDraft, string>>;
  columns: string[];
}

/**
 * RFC 4180 CSV: quoted fields may hold commas, newlines and doubled quotes.
 * Splitting on commas and newlines breaks on the first answer that contains a
 * comma, which is most of them.
 */
export function parseCsv(text: string): Row[] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let quoted = false;
  const src = text.replace(/^﻿/, '');   // Excel's byte-order mark

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else field += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ',') { record.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      record.push(field); field = '';
      if (record.some((f) => f.trim() !== '')) records.push(record);
      record = [];
    } else field += ch;
  }
  record.push(field);
  if (record.some((f) => f.trim() !== '')) records.push(record);
  if (quoted) throw new Error('The file ends inside a quoted field — a closing " is missing.');

  const [header, ...rows] = records;
  if (!header) return [];
  const keys = header.map((h) => h.trim());
  return rows.map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? '').trim()])));
}

export function parseRows(fileName: string, text: string): Row[] {
  const name = fileName.toLowerCase();
  if (name.endsWith('.jsonl') || name.endsWith('.ndjson')) {
    return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l, i) => {
      try { return JSON.parse(l) as Row; } catch { throw new Error(`Line ${i + 1} is not valid JSON.`); }
    });
  }
  if (name.endsWith('.json')) {
    const parsed = JSON.parse(text);
    const rows = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.data) ? parsed.data : null;
    if (!rows) throw new Error('A JSON dataset must be an array of objects (or {"data": [...]}).');
    return rows as Row[];
  }
  return parseCsv(text);
}

/** The names people give each column, most specific first. Compared loosely. */
const ALIASES: Record<keyof Omit<CaseDraft, 'row'>, string[]> = {
  caseCode: ['casecode', 'case_code', 'code', 'id'],
  name: ['name', 'title'],
  input: ['input', 'question', 'query', 'prompt', 'task', 'user_input'],
  expectedResult: ['expectedresult', 'expected_result', 'expected', 'expected_output', 'expected_answer',
                   'answer', 'ground_truth', 'reference'],
  // Not 'output': in an exported run that column is what the MODEL said, and
  // importing it as the expectation would grade every answer against itself.
  expectedSources: ['expectedsources', 'expected_sources', 'sources', 'source', 'expected_documents'],
  context: ['context', 'ground_truth_context', 'passage', 'passages', 'reference_context'],
  requiredFacts: ['requiredfacts', 'required_facts', 'facts', 'must_include'],
  expectedToolNames: ['expectedtoolcalls', 'expected_tool_calls', 'expected_tools', 'tools_expected', 'tool_calls'],
  tags: ['tags', 'labels'],
};

const loose = (k: string) => k.toLowerCase().replace(/[\s-]/g, '_');

/** A list cell: a real array, a JSON array, or `a | b | c`. */
function asList(v: unknown): string[] {
  if (v === null || v === undefined || v === '') return [];
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === 'object' && x ? String((x as Row).name ?? (x as Row).sourceRef ?? JSON.stringify(x)) : String(x)))
      .map((s) => s.trim()).filter(Boolean);
  }
  const s = String(v).trim();
  if (s.startsWith('[')) {
    try { return asList(JSON.parse(s)); } catch { /* fall through to splitting */ }
  }
  return s.split(/\s*[|;]\s*/).filter(Boolean);
}

const asText = (v: unknown): string =>
  v === null || v === undefined ? '' : typeof v === 'string' ? v.trim() : JSON.stringify(v);

/** Case codes are per-project unique and end up in URLs and logs. */
export const safeCode = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9_.-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 140);

export function toDrafts(rows: Row[], codePrefix: string): ParsedDataset {
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const byLoose = new Map(columns.map((c) => [loose(c), c]));
  const mapping: ParsedDataset['mapping'] = {};
  for (const [field, names] of Object.entries(ALIASES) as [keyof CaseDraft, string[]][]) {
    const hit = names.find((n) => byLoose.has(n));
    if (hit) mapping[field] = byLoose.get(hit);
  }

  const drafts: CaseDraft[] = [];
  const skipped: ParsedDataset['skipped'] = [];
  const seen = new Set<string>();
  const pick = (r: Row, f: keyof CaseDraft) => (mapping[f] ? r[mapping[f] as string] : undefined);

  rows.forEach((r, i) => {
    const row = i + 1;
    const input = asText(pick(r, 'input'));
    if (!input) {
      skipped.push({ row, reason: mapping.input ? `empty "${mapping.input}"` : 'no question/input column' });
      return;
    }
    const given = asText(pick(r, 'caseCode'));
    // A bare row number (1, 2, 3…) is unique only inside this file; as a case code
    // it collides with the next dataset imported into the same project.
    const caseCode = safeCode(
      given && !/^\d+$/.test(given) ? given : `${codePrefix}_${String(given || row).padStart(4, '0')}`);
    if (seen.has(caseCode)) { skipped.push({ row, reason: `case code "${caseCode}" repeats an earlier row` }); return; }
    seen.add(caseCode);

    const context = pick(r, 'context');
    drafts.push({
      row, caseCode, input,
      name: asText(pick(r, 'name')) || (input.length > 80 ? `${input.slice(0, 77)}…` : input),
      expectedResult: asText(pick(r, 'expectedResult')) || null,
      expectedSources: asList(pick(r, 'expectedSources')),
      // One passage per cell; a list only when the file already says it is one.
      context: Array.isArray(context) ? asList(context) : asText(context) ? [asText(context)] : [],
      requiredFacts: asList(pick(r, 'requiredFacts')),
      expectedToolNames: asList(pick(r, 'expectedToolNames')),
      tags: asList(pick(r, 'tags')),
    });
  });
  return { drafts, skipped, mapping, columns };
}
